import { config } from '@iheartjane/config';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
/* eslint-disable-next-line no-restricted-imports */
import * as t from 'io-ts';

import type { Manager, StaffMember } from '@jane/business-admin/types';
import { tManager, tStaffMember } from '@jane/business-admin/types';
import { fetchWithDecode, removeRetailOrg } from '@jane/business-admin/util';
import { getJwt, removeJwt, setJwt } from '@jane/shared/auth';
import { api, janeApiV2 } from '@jane/shared/data-access';

interface LoginData {
  manager: {
    email?: string;
    password?: string;
  };
}

const sessionUrl = () => `${config.apiPathV2}/managers/sessions`;

const login = async (
  loginData: LoginData
): Promise<{ authHeader: string | null; manager: Manager }> => {
  const response = await api.post<Response>(
    sessionUrl(),
    loginData,
    undefined,
    true
  );
  const { manager } = await response.json();
  return {
    authHeader: response.headers.get('Authorization'),
    manager,
  };
};
export const useLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: login,
    mutationKey: ['login'],
    onError: (error) => {
      console.error(error);
    },
    onSuccess: (data) => {
      // Clear any data from a previous session
      queryClient.clear();

      if (data.authHeader) {
        queryClient.setQueryData(['jwt'], data.authHeader);
        setJwt(data.authHeader);
      }
      queryClient.setQueryData(['manager'], data);
    },
  });
};

const logout = async () => await api.delete(sessionUrl());
export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: logout,
    mutationKey: ['logout'],
    onSuccess: () => {
      janeMixpanel.reset();
      queryClient.invalidateQueries(['manager']);
      queryClient.invalidateQueries(['jwt']);
      removeJwt();
      queryClient.invalidateQueries(['retail-org']);
      removeRetailOrg();
    },
  });
};

const whoAmIUrl = () => `${config.apiPathV2}/managers/whoami`;

const currentManagerRequest = async () => {
  const data = await fetchWithDecode(
    api.get(whoAmIUrl()),
    t.type({ manager: t.union([tManager, t.UnknownRecord]) }),
    whoAmIUrl()
  );
  return data;
};
// Used as a general query to keep track of manager credentials
// Can be modified by requests from /whoami and /login
export const useManager = () =>
  useQuery<{ manager: Manager }>({
    queryFn: currentManagerRequest,
    queryKey: ['manager'],
    staleTime: Infinity,
  });

// Persist jwt token even when pages are routed
const jwtRequest = async (): Promise<string | null | undefined> => {
  return new Promise((res) => {
    const jwt = getJwt();
    return res(jwt);
  });
};
export const useGetJwt = () =>
  useQuery({ queryFn: jwtRequest, queryKey: ['jwt'], staleTime: Infinity });

const updateProfileUrl = () => `${config.apiPathV2}/managers/profile`;

export interface UpdateProfileParams {
  email?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
}

const updateProfile = async (manager: UpdateProfileParams) =>
  api.patch(updateProfileUrl(), { manager });
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateProfileParams) => updateProfile(data),
    onError: (error) => {
      console.error(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['manager']);
    },
  });
};

// SSO
interface SsoDiscoverResponse {
  authorize_url?: string;
  provider_name?: string;
  sso_required: boolean;
}

const ssoDiscoverUrl = () => `${config.apiPathV2}/managers/sso/discover`;

const ssoDiscover = async (email: string): Promise<SsoDiscoverResponse> => {
  return api.post<SsoDiscoverResponse>(ssoDiscoverUrl(), { email });
};

export const useSsoDiscover = () => {
  return useMutation({
    mutationFn: (email: string) => ssoDiscover(email),
    mutationKey: ['sso-discover'],
    onError: (error) => {
      console.error(error);
    },
  });
};

// SSO token exchange
interface SsoTokenResponse {
  token: string;
}

const ssoTokenUrl = () => `${config.apiPathV2}/managers/sso/token`;

const ssoExchangeToken = async (code: string): Promise<SsoTokenResponse> => {
  return api.post<SsoTokenResponse>(ssoTokenUrl(), { code });
};

export const useSsoToken = () => {
  return useMutation({
    mutationFn: (code: string) => ssoExchangeToken(code),
    mutationKey: ['sso-token'],
  });
};

export type RequestPasswordResetData = {
  email: string;
};
const requestPasswordReset = async (
  data: RequestPasswordResetData
): Promise<RequestPasswordResetData> => {
  return janeApiV2.post<RequestPasswordResetData>(
    `/business/reset_password_request`,
    {
      reset_password: data,
    }
  );
};
export const useRequestPasswordReset = () => {
  return useMutation({
    mutationFn: (data: RequestPasswordResetData) => {
      return requestPasswordReset(data);
    },
    onError: (error) => {
      console.error(error);
    },
  });
};

export type ResetPasswordData = {
  password: string;
  password_confirmation: string;
  reset_password_token: string;
};
const resetPassword = async (
  data: ResetPasswordData
): Promise<ResetPasswordData> => {
  return janeApiV2.patch<ResetPasswordData>(`/business/reset_password`, {
    reset_password: data,
  });
};
export const useResetPassword = () => {
  return useMutation({
    mutationFn: (data: ResetPasswordData) => {
      const path = window.location.pathname;
      data.reset_password_token = path.substring(path.lastIndexOf('/') + 1);
      return resetPassword(data);
    },
    onError: (error) => {
      console.error(error);
    },
  });
};

const updatePasswordUrl = () => `${config.apiPathV2}/managers/passwords`;

interface UpdatePasswordParams {
  current_password: string;
  password: string;
  password_confirmation: string;
}

const updatePassword = async (manager: UpdatePasswordParams) =>
  api.patch(updatePasswordUrl(), { manager });
export const useUpdatePassword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdatePasswordParams) => updatePassword(data),
    onError: (error) => {
      console.error(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['manager']);
    },
  });
};

const invitesUrl = () => `${config.apiPathV2}/managers/invites`;

const invitationRequest = (invitationToken: string) =>
  fetchWithDecode(
    api.get(`${invitesUrl()}/${invitationToken}`),
    tStaffMember,
    `${invitesUrl()}/${invitationToken}`
  );

export const useInvitation = (invitationToken: string) =>
  useQuery<StaffMember>({
    enabled: Boolean(invitationToken),
    queryFn: () => invitationRequest(invitationToken),
    queryKey: ['manager_invitation', invitationToken],
    staleTime: Infinity,
  });

export type CompleteRegistrationParams = Pick<
  StaffMember,
  | 'first_name'
  | 'last_name'
  | 'password'
  | 'phone'
  | 'accepted_terms_and_conditions'
>;

const completeRegistration = (
  invitationToken: string,
  manager: CompleteRegistrationParams
) =>
  api.patch(`${invitesUrl()}/${invitationToken}`, {
    manager,
  });
export const useCompleteRegistration = (invitationToken: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (manager: CompleteRegistrationParams) =>
      completeRegistration(invitationToken, manager),
    onError: (error) => {
      console.error(error);
    },
    onSuccess: () => {
      queryClient.removeQueries(['manager_invitation', invitationToken]);
    },
  });
};
